const ACTIVE_RUNS = new Set(['running', 'pending']);
const KNOWN_HEALTH = new Set(['ok', 'off', 'failed', 'paused', 'late']);

function loadProblem(pipeline, health) {
  if (pipeline.paused_by_billing) return 'billing';
  if (pipeline.paused_by_connection) return 'source';
  if (pipeline.is_active === false) return null;
  if (pipeline.source_connection_status === 'error') return 'source';
  if (pipeline.last_run?.status === 'failed') return 'failed';
  if (['failed', 'paused', 'late'].includes(health?.state)) return health.state;
  return null;
}

/** A successful recent run alone cannot confirm other loads or schedules. */
export function getLoadSummary(pipelinesQuery, healthQuery) {
  if (pipelinesQuery.error) return { state: 'unavailable' };
  if (!pipelinesQuery.isSuccess || !Array.isArray(pipelinesQuery.data)) {
    return { state: pipelinesQuery.isLoading ? 'checking' : 'unknown' };
  }
  const pipelines = pipelinesQuery.data;
  if (!pipelines.length) return { state: 'empty' };
  const healthById = Object.fromEntries(
    (Array.isArray(healthQuery.data) ? healthQuery.data : []).map((health) => [health.public_id, health]),
  );
  // Current pipeline flags and a failed latest run remain useful while health
  // is unavailable; cached health after a failed refresh cannot confirm green.
  const canUseHealth = healthQuery.isSuccess && !healthQuery.error;
  const problems = pipelines.flatMap((pipeline) => {
    const reason = loadProblem(pipeline, canUseHealth ? healthById[pipeline.public_id] : null);
    return reason ? [{ pipeline, reason }] : [];
  });
  if (problems.length) return { state: 'attention', problems };
  if (healthQuery.error) return { state: 'unavailable' };
  if (!healthQuery.isSuccess) return { state: healthQuery.isLoading ? 'checking' : 'unknown' };
  if (!pipelines.every((pipeline) => KNOWN_HEALTH.has(healthById[pipeline.public_id]?.state))) {
    return { state: 'unknown' };
  }
  const active = pipelines.filter((pipeline) => pipeline.is_active !== false
    && healthById[pipeline.public_id].state !== 'off');
  const offCount = pipelines.length - active.length;
  if (!active.length) return { state: 'off', offCount };
  if (active.some((pipeline) => ACTIVE_RUNS.has(pipeline.last_run?.status))) return { state: 'processing', offCount };
  if (active.some((pipeline) => !pipeline.last_run)) return { state: 'waiting', offCount };
  if (active.some((pipeline) => pipeline.last_run.status !== 'success')) return { state: 'unknown', offCount };
  return { state: 'healthy', offCount };
}

/**
 * Values for the load cards, scoped to the same complete list as the summary.
 * `knownProblems` is only a lower bound when health is incomplete; `problems`
 * stays unknown in that case. A timestamp is a recorded successful arrival,
 * never confirmation that every source, model or report is current.
 */
export function getLoadMetrics(pipelinesQuery, healthQuery) {
  const listReady = pipelinesQuery.isSuccess && !pipelinesQuery.error && Array.isArray(pipelinesQuery.data);
  const empty = { total: null, problems: null, knownProblems: 0, latestDataAt: null, healthComplete: false };
  if (!listReady) return empty;

  const pipelines = pipelinesQuery.data;
  const healthReady = healthQuery.isSuccess && !healthQuery.error && Array.isArray(healthQuery.data);
  const healthById = Object.fromEntries(
    (healthReady ? healthQuery.data : []).map((health) => [health.public_id, health]),
  );
  const healthComplete = Boolean(healthReady
    && pipelines.every((pipeline) => KNOWN_HEALTH.has(healthById[pipeline.public_id]?.state)));
  const summary = getLoadSummary(pipelinesQuery, healthQuery);
  const knownProblems = summary.problems?.length || 0;
  const arrivals = healthComplete ? pipelines.flatMap((pipeline) => {
    const value = healthById[pipeline.public_id].last_data_at;
    const timestamp = typeof value === 'string' && value.trim() ? Date.parse(value) : NaN;
    return Number.isFinite(timestamp) ? [timestamp] : [];
  }) : [];

  return {
    total: pipelines.length,
    problems: healthComplete ? knownProblems : null,
    knownProblems,
    latestDataAt: arrivals.length ? new Date(Math.max(...arrivals)).toISOString() : null,
    healthComplete,
  };
}

/** Connected source-capable endpoints; an access test is not data freshness. */
export function getSourceMetrics(connectionsQuery) {
  if (!connectionsQuery.isSuccess || connectionsQuery.error || !Array.isArray(connectionsQuery.data)) {
    return { total: null, active: null };
  }
  const sources = connectionsQuery.data.filter((connection) => {
    const type = connection.connector_type;
    return type?.plugin_kind !== 'loader' && typeof type?.meltano_tap === 'string' && Boolean(type.meltano_tap.trim());
  });
  return { total: sources.length, active: sources.filter((connection) => connection.status === 'active').length };
}

/** Latest preparation outcomes, deliberately not report/data freshness. */
export function getPreparationSummary(query) {
  if (query.error) return { state: 'unavailable' };
  if (!query.isSuccess || !Array.isArray(query.data)) return { state: query.isLoading ? 'checking' : 'unknown' };
  const projects = query.data;
  if (!projects.length) return { state: 'empty' };
  const problems = projects.filter((project) => project.paused_by_billing
    || (project.is_active !== false && (project.last_run?.status === 'failed'
      || (project.status === 'error' && !ACTIVE_RUNS.has(project.last_run?.status)))));
  if (problems.length) return { state: 'attention', projects: problems };
  const active = projects.filter((project) => project.is_active !== false && project.status !== 'disabled');
  if (!active.length) return { state: 'off' };
  if (active.some((project) => ACTIVE_RUNS.has(project.last_run?.status))) return { state: 'processing' };
  if (active.some((project) => !project.last_run)) return { state: 'waiting' };
  if (active.some((project) => project.last_run.status !== 'success')) return { state: 'unknown' };
  return { state: 'completed' };
}
